import React from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Button, HStack, useColorModeValue, Badge, Text, Flex, Icon, VStack, Stack, Box, Heading
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, TimeIcon, QuestionIcon } from '@chakra-ui/icons';
import { FiMessageCircle } from 'react-icons/fi';

/**
 * Detail Item Component for Modal
 */
const DetailItem = ({ label, value }) => (
  <Box mb={2}>
    <Text fontWeight="bold" fontSize="sm" color="gray.500">
      {label}
    </Text>
    <Text>{value}</Text>
  </Box>
);

/**
 * Status Badge Component
 */
const StatusBadge = ({ isCompleted, isInProgress, isPendingConfirmation }) => {
  let colorScheme, status, icon;

  if (isCompleted) {
    colorScheme = 'green';
    status = 'Concluído';
    icon = CheckIcon;
  } else if (isPendingConfirmation) {
    colorScheme = 'orange';
    status = 'Aguardando Confirmação';
    icon = QuestionIcon;
  } else if (isInProgress) {
    colorScheme = 'yellow';
    status = 'Em Andamento';
    icon = TimeIcon;
  } else {
    colorScheme = 'red';
    status = 'Aguardando';
    icon = WarningIcon;
  }

  return (
    <Badge colorScheme={colorScheme} px={2} py={1} borderRadius="full">
      <Flex align="center">
        <Icon as={icon} mr={1} boxSize="10px" />
        <Text>{status}</Text>
      </Flex>
    </Badge>
  );
};

/**
 * Message Bubble Component
 */
const MessageBubble = ({ message, userName }) => {
  const isUser = message.senderName === userName;
  const bubbleBg = useColorModeValue(
    isUser ? 'purple.50' : 'blue.50',
    isUser ? 'purple.900' : 'blue.900'
  );
  const borderColor = useColorModeValue(
    isUser ? 'purple.200' : 'blue.200',
    isUser ? 'purple.700' : 'blue.700'
  );
  
  return (
    <Box
      bg={bubbleBg}
      p={3}
      mb={2}
      borderRadius="md"
      maxW="80%"
      ml={isUser ? 'auto' : '0'}
      borderWidth="1px"
      borderColor={borderColor}
    >
      <Text fontWeight="bold">{message.senderName || 'Usuário'}</Text>
      <Text>{message.content || 'Mensagem indisponível'}</Text>
      <Text fontSize="xs" color="gray.500" textAlign="right">
        {message.timestamp ? new Date(message.timestamp).toLocaleString() : 'Horário indisponível'}
      </Text>
    </Box>
  );
};

/**
 * WorkerHelpDetailsModal Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.helpData - Data for the help request
 * @param {Function} props.onActionTaken - Function to handle action taken
 * @param {Function} props.onOpenChat - Function to open the chat
 */
const WorkerHelpDetailsModal = ({ isOpen, onClose, helpData, onActionTaken, onOpenChat }) => {
  // Colors
  const modalHeaderBg = useColorModeValue('purple.50', 'purple.900');
  const modalHeaderColor = useColorModeValue('purple.700', 'purple.100');
  const mainBoxBg = useColorModeValue('gray.50', 'gray.700');
  const reasonBoxBg = useColorModeValue('red.50', 'red.900');
  const serviceBoxBg = useColorModeValue('blue.50', 'blue.900');
  const conversationBoxBg = useColorModeValue('purple.50', 'purple.900');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');
  const messageBg = useColorModeValue('white', 'gray.800');

  if (!helpData) return null;

  const {
    _id,
    userName,
    reason,
    additionalNotes,
    isConversation,
    conversation,
    serviceDetails,
    actionTaken,
    inProgress,
    pendingConfirmation,
    createdAt
  } = helpData;

  const hasMessages = conversation && conversation.messages && conversation.messages.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="md">
        <ModalHeader 
          bg={modalHeaderBg}
          color={modalHeaderColor}
          borderTopRadius="md"
          py={4}
        >
          <HStack>
            <Text>Detalhes da Solicitação de Ajuda</Text>
            <StatusBadge
              isCompleted={actionTaken}
              isInProgress={inProgress}
              isPendingConfirmation={pendingConfirmation}
            />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={4}>
          <VStack align="stretch" spacing={4}>
            {/* Main Information */}
            <Box 
              p={4} 
              bg={mainBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="purple.500">
                Informações Principais
              </Heading>
              <Stack spacing={3}>
                <Flex flexWrap="wrap" justify="space-between">
                  <DetailItem label="ID" value={_id || 'N/A'} />
                  <DetailItem 
                    label="Data de Criação" 
                    value={
                      createdAt
                        ? new Date(createdAt).toLocaleString()
                        : 'N/A'
                    } 
                  />
                </Flex>
                <DetailItem label="Usuário" value={userName || 'N/A'} />
              </Stack>
            </Box>
            
            {/* Reason Box */}
            <Box 
              p={4} 
              bg={reasonBoxBg} 
              borderRadius="md"
              borderWidth="1px"
              borderColor={filterBorderColor}
            >
              <Heading as="h3" size="sm" mb={3} color="red.500">
                Razão da Solicitação
              </Heading>
              <DetailItem label="Motivo" value={reason || 'N/A'} />
              {additionalNotes && (
                <DetailItem label="Notas Adicionais" value={additionalNotes} />
              )}
            </Box>
            
            {/* Service Details */}
            {!isConversation && serviceDetails && (
              <Box 
                p={4} 
                bg={serviceBoxBg} 
                borderRadius="md"
                borderWidth="1px"
                borderColor={filterBorderColor}
              >
                <Heading as="h3" size="sm" mb={3} color="blue.500">
                  Detalhes do Serviço
                </Heading>
                <Stack spacing={3}>
                  <DetailItem 
                    label="Descrição" 
                    value={serviceDetails?.description || 'N/A'} 
                  />
                  <Flex flexWrap="wrap" justify="space-between">
                    <DetailItem 
                      label="Status" 
                      value={serviceDetails?.status || 'N/A'} 
                    />
                    <DetailItem 
                      label="Valor" 
                      value={
                        serviceDetails?.value 
                          ? `R$ ${serviceDetails.value.toFixed(2)}` 
                          : 'N/A'
                      } 
                    />
                  </Flex>
                  <Flex flexWrap="wrap" justify="space-between">
                    <DetailItem 
                      label="Data de Início" 
                      value={serviceDetails?.startDate 
                        ? new Date(serviceDetails.startDate).toLocaleDateString()
                        : 'N/A'
                      } 
                    />
                    <DetailItem 
                      label="Data de Término" 
                      value={serviceDetails?.endDate 
                        ? new Date(serviceDetails.endDate).toLocaleDateString()
                        : 'N/A'
                      } 
                    />
                  </Flex>
                </Stack>
              </Box>
            )}
            
            {/* Conversation Messages (if available) */}
            {isConversation && hasMessages && (
              <Box 
                p={4} 
                bg={conversationBoxBg} 
                borderRadius="md"
                borderWidth="1px"
                borderColor={filterBorderColor}
              >
                <Heading as="h3" size="sm" mb={3} color="purple.500">
                  Conversas
                </Heading>
                
                <Box
                  maxH="400px"
                  overflowY="auto"
                  display="flex"
                  flexDirection="column"
                  p={3}
                  borderWidth="1px"
                  borderColor={filterBorderColor}
                  borderRadius="md"
                  bg={messageBg}
                >
                  {conversation.messages.map((message, index) => (
                    <MessageBubble 
                      key={index} 
                      message={message} 
                      userName={userName} 
                    />
                  ))}
                </Box>
                
                {conversation?.participants && conversation.participants.length > 0 && (
                  <Box mt={3}>
                    <Text fontWeight="bold" fontSize="sm" color="gray.500" mb={2}>
                      Participantes:
                    </Text>
                    <Stack spacing={2}>
                      {conversation.participants.map((participant, index) => (
                        <Text key={index}>
                          <strong>{participant?.name || 'Usuário'}</strong>
                          {participant?.email && ` (${participant.email})`}
                        </Text>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor={filterBorderColor}>
          <HStack spacing={3}>
            {inProgress && !actionTaken && !pendingConfirmation && (
              <Button
                leftIcon={<CheckIcon />}
                colorScheme="green"
                onClick={() => onActionTaken(_id)}
              >
                Finalizar
              </Button>
            )}
            {pendingConfirmation && !actionTaken && (
              <Badge colorScheme="orange" px={3} py={2} borderRadius="md">
                <Flex align="center">
                  <Icon as={QuestionIcon} mr={2} />
                  <Text>Aguardando resposta do trabalhador</Text>
                </Flex>
              </Badge>
            )}
            <Button
              leftIcon={<FiMessageCircle />}
              colorScheme="teal"
              variant="outline"
              onClick={() => onOpenChat(_id)}
            >
              Chat com Usuário
            </Button>
            <Button onClick={onClose} variant="ghost">
              Fechar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WorkerHelpDetailsModal;
